import { useState } from "react";
import { Link } from "react-router-dom";
import { Download, FileText, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { downloadPortalDocument, type PortalRequiredDocument } from "@/features/portal/api";
import { portalHref } from "@/features/portal/portal-base";
import type { PortalDocument } from "@/features/portal/types";

function formatDocDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

/** "My documents" — what the firm has shared (downloadable) plus the still-pending
 * requested documents rendered as "waiting for you" rows. */
export function DocumentsCard({
  documents,
  pendingDocs,
}: {
  documents: PortalDocument[];
  pendingDocs: PortalRequiredDocument[];
}) {
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  async function handleDownload(doc: PortalDocument) {
    setDownloadingId(doc.id);
    try {
      const blob = await downloadPortalDocument(doc.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error((err as { message?: string })?.message ?? "Download failed.");
    } finally {
      setDownloadingId(null);
    }
  }

  const empty = documents.length === 0 && pendingDocs.length === 0;

  return (
    <Card className="gap-0 overflow-hidden p-0">
      <div className="flex items-baseline justify-between gap-3 px-6 pt-6 pb-4">
        <div>
          <h3 className="font-heading text-xl font-bold text-foreground">My documents</h3>
          <p className="text-muted-foreground mt-0.5 text-sm">
            Everything you've shared, and what we've sent you.
          </p>
        </div>
        {documents.length > 0 && (
          <Link
            to={portalHref("/documents")}
            className="text-info shrink-0 text-sm font-semibold hover:underline"
          >
            View all ({documents.length})
          </Link>
        )}
      </div>

      {empty ? (
        <div className="flex flex-col items-center gap-2 px-6 pt-2 pb-10 text-center">
          <div className="bg-accent text-accent-foreground grid size-11 place-items-center rounded-sm">
            <FileText className="size-5" />
          </div>
          <p className="text-muted-foreground text-sm">
            No documents yet. Anything you upload or we share will show up here.
          </p>
        </div>
      ) : (
        <ul className="flex flex-col gap-2 px-6 pb-6">
          {documents.slice(0, 4).map((doc) => (
            <li
              key={`doc-${doc.id}`}
              className="flex items-center gap-3 rounded-md border px-3.5 py-3"
            >
              <div className="bg-accent text-accent-foreground grid size-10 shrink-0 place-items-center rounded-sm text-[11px] font-bold">
                PDF
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium text-foreground">{doc.name}</div>
                <div className="text-muted-foreground text-xs">Shared · {formatDocDate(doc.createdAt)}</div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                disabled={downloadingId === doc.id}
                onClick={() => handleDownload(doc)}
              >
                <Download className="size-4" />
                Download
              </Button>
            </li>
          ))}
          {pendingDocs.slice(0, 2).map((doc) => (
            <li
              key={`pending-${doc.id}`}
              className="bg-muted/40 flex items-center gap-3 rounded-md border-2 border-dashed px-3.5 py-3"
            >
              <div className="bg-card text-muted-foreground grid size-10 shrink-0 place-items-center rounded-sm border">
                <Plus className="size-5" />
              </div>
              <div className="min-w-0 flex-1 text-sm">
                <span className="truncate text-foreground">{doc.name}</span> —{" "}
                <strong className="text-warning">waiting for you</strong>
              </div>
              <Link
                to={portalHref("/requested-documents")}
                className="text-info shrink-0 text-sm font-semibold hover:underline"
              >
                Upload now
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
